import {
  HttpContext,
  HttpContextToken,
  HttpRequest,
} from '@angular/common/http';

/**
 * Token to mark requests that should retry after re-authentication on 401
 *
 * When set to true, the auth interceptor will:
 * 1. Close any active loading dialogs
 * 2. Open the authentication dialog
 * 3. Retry the original request after successful authentication
 * 4. Only logout if authentication fails or is cancelled
 *
 * @default false
 */
export const RETRY_ON_UNAUTHORIZED = new HttpContextToken<boolean>(() => false);

/**
 * Token to provide a user-friendly message when auth is required
 *
 * This message is shown to the user when the auth dialog opens
 * due to a 401 response. If not provided, a default message is used.
 *
 * @default null
 */
export const AUTH_REQUIRED_MESSAGE = new HttpContextToken<string | null>(
  () => null,
);

export const RETRYING_REQUEST_MESSAGE = new HttpContextToken<string | null>(
  () => null,
);

/**
 * Token to mark requests whose 401 is a legitimate answer rather than an
 * expired session — a credential check being the canonical case.
 *
 * The auth interceptor treats any other 401 as "the session died" and responds
 * by clearing local auth and navigating away. For a request that is *asking*
 * whether credentials are valid, a 401 is the expected negative answer and must
 * leave the caller where it is, free to render its own error.
 *
 * @default false
 */
export const EXPECTED_UNAUTHORIZED = new HttpContextToken<boolean>(() => false);

/**
 * Helper to create HttpContext for requests that need auth retry on 401
 *
 * @param message - Optional custom message to show when auth is required
 * @returns HttpContext with RETRY_ON_UNAUTHORIZED set to true
 *
 * @example
 * ```ts
 * this.http.post('/api/orders', data, {
 *   context: withAuthRetry('Tu sesión expiró. Inicia sesión para continuar.')
 * })
 * ```
 */
export function withAuthRetry(
  message?: string,
  retryingMessage?: string,
): HttpContext {
  const context = new HttpContext();
  context.set(RETRY_ON_UNAUTHORIZED, true);

  if (message) {
    context.set(AUTH_REQUIRED_MESSAGE, message);
  } else {
    context.set(
      AUTH_REQUIRED_MESSAGE,
      'Tu sesión expiró, ingresa nuevamente para continuar.',
    );
  }

  if (retryingMessage) {
    context.set(RETRYING_REQUEST_MESSAGE, retryingMessage);
  } else {
    context.set(RETRYING_REQUEST_MESSAGE, 'Reintentando...');
  }
  return context;
}

export function getAuthRetry(req: HttpRequest<unknown>) {
  return {
    shouldRetry: req.context.get(RETRY_ON_UNAUTHORIZED),
    message: req.context.get(AUTH_REQUIRED_MESSAGE),
    retryingMessage: req.context.get(RETRYING_REQUEST_MESSAGE),
  };
}

/**
 * Helper to create HttpContext for a request whose 401 is an expected answer
 *
 * @returns HttpContext with EXPECTED_UNAUTHORIZED set to true
 *
 * @example
 * ```ts
 * this.http.post('/api/auth/login/session', credentials, {
 *   context: withExpectedUnauthorized(),
 * })
 * ```
 */
export function withExpectedUnauthorized(): HttpContext {
  const context = new HttpContext();
  context.set(EXPECTED_UNAUTHORIZED, true);
  return context;
}

export function isExpectedUnauthorized(req: HttpRequest<unknown>): boolean {
  return req.context.get(EXPECTED_UNAUTHORIZED);
}
